import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Plus,
  Box,
  Layers,
  Archive,
  Package,
  Edit3,
  Trash2,
  LayoutGrid,
  Printer,
  Loader2,
  AlertCircle,
  RefreshCw,
  ClipboardCheck,
} from 'lucide-react';
import QConfiguratorSidebar from './QConfiguratorSidebar';
import QConfiguratorModuleView from './QConfiguratorModuleView';
import QConfiguratorSummary from './QConfiguratorSummary';
import {
  calculateUsedUnits,
  MODULE_COLORS,
} from './qconfigurator.types';
import './QConfigurator.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

// Transform API cabinet to frontend format (Cabinet -> Modules -> Drawers -> Items)
const transformCabinetFromAPI = (cabinet) => ({
  id: cabinet.id,
  name: cabinet.name,
  color: cabinet.color || '#f97316',
  createdAt: cabinet.created_at,
  updatedAt: cabinet.updated_at,
  modules: (cabinet.modules || []).map(module => ({
    id: module.id,
    name: module.name,
    color: module.color || '#f97316',
    order: module.order,
    drawers: (module.drawers || []).map(drawer => ({
      id: drawer.id,
      name: drawer.name,
      size: drawer.size,
      units: drawer.units,
      image: drawer.image,
      order: drawer.order,
      items: (drawer.items || []).map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        image: item.image,
        warehouseItemId: item.warehouse_item,
      })),
    })),
  })),
});

// Transform frontend cabinet to API format
const transformCabinetToAPI = (cabinet) => ({
  name: cabinet.name,
  color: cabinet.color,
  modules: (cabinet.modules || []).map((module, modIdx) => ({
    name: module.name,
    color: module.color || '#f97316',
    order: module.order ?? modIdx,
    drawers: (module.drawers || []).map((drawer, drwIdx) => ({
      name: drawer.name,
      size: drawer.size,
      image: drawer.image,
      order: drawer.order ?? drwIdx,
      items: (drawer.items || []).map(item => ({
        name: item.name,
        description: item.description || '',
        quantity: item.quantity || 1,
        image: item.image,
        warehouse_item: item.warehouseItemId || null,
      })),
    })),
  })),
});

const QConfiguratorPro = () => {
  const navigate = useNavigate();
  const [cabinets, setCabinets] = useState([]);
  const [selectedCabinetId, setSelectedCabinetId] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'editor', 'summary'
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCabinetsForPrint, setSelectedCabinetsForPrint] = useState(new Set());

  // Fetch cabinets from API
  const fetchCabinets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/cabinets/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = Array.isArray(response.data) ? response.data :
        response.data.results ? response.data.results : [];
      setCabinets(data.map(transformCabinetFromAPI));
    } catch (err) {
      console.error('Error fetching cabinets:', err);
      setError('Failed to load cabinets. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCabinets();
  }, [fetchCabinets]);

  // Create new cabinet (Szafka)
  const handleAddCabinet = useCallback(async () => {
    setIsSaving(true);
    try {
      const newCabinet = {
        name: `Szafka ${cabinets.length + 1}`,
        color: '#0f172a',
        modules: [],
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/warehouse/cabinets/`,
        transformCabinetToAPI(newCabinet),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const createdCabinet = transformCabinetFromAPI(response.data);
      setCabinets(prev => [...prev, createdCabinet]);
      setSelectedCabinetId(createdCabinet.id);
      setViewMode('editor');
    } catch (err) {
      console.error('Error creating cabinet:', err);
      alert('Failed to create cabinet. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [cabinets.length]);

  // Add module to selected cabinet
  const handleAddModule = useCallback(async () => {
    if (!selectedCabinetId) return;

    const cabinet = cabinets.find(c => c.id === selectedCabinetId);
    if (!cabinet) return;

    const newModule = {
      name: `Module ${(cabinet.modules || []).length + 1}`,
      color: MODULE_COLORS[Math.floor(Math.random() * MODULE_COLORS.length)],
      drawers: [],
    };

    const updatedCabinet = {
      ...cabinet,
      modules: [...(cabinet.modules || []), newModule],
    };

    // Optimistic update
    setCabinets(prev => prev.map(c => c.id === selectedCabinetId ? updatedCabinet : c));

    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/warehouse/cabinets/${selectedCabinetId}/`,
        transformCabinetToAPI(updatedCabinet),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const updated = transformCabinetFromAPI(response.data);
      setCabinets(prev => prev.map(c => c.id === updated.id ? updated : c));

      // Select the new module
      if (updated.modules && updated.modules.length > 0) {
        setSelectedModuleId(updated.modules[updated.modules.length - 1].id);
      }
    } catch (err) {
      console.error('Error adding module:', err);
      fetchCabinets();
      alert('Failed to add module. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedCabinetId, cabinets, fetchCabinets]);

  // Delete cabinet
  const handleDeleteCabinet = useCallback(async (cabinetId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/warehouse/cabinets/${cabinetId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      setCabinets(prev => prev.filter(c => c.id !== cabinetId));
      if (selectedCabinetId === cabinetId) {
        setSelectedCabinetId(null);
        setSelectedModuleId(null);
        setViewMode('cabinet-list');
      }
    } catch (err) {
      console.error('Error deleting cabinet:', err);
      alert('Failed to delete cabinet. Please try again.');
    }
  }, [selectedCabinetId]);

  // Delete module from cabinet
  const handleDeleteModule = useCallback(async (moduleId) => {
    if (!selectedCabinetId) return;

    const cabinet = cabinets.find(c => c.id === selectedCabinetId);
    if (!cabinet) return;

    // Find index of currently selected module (if not the one being deleted)
    const selectedModuleIndex = selectedModuleId && selectedModuleId !== moduleId
      ? (cabinet.modules || []).findIndex(m => m.id === selectedModuleId)
      : -1;

    // Adjust index if deleted module is before the selected one
    const deletedModuleIndex = (cabinet.modules || []).findIndex(m => m.id === moduleId);
    const adjustedIndex = selectedModuleIndex > deletedModuleIndex ? selectedModuleIndex - 1 : selectedModuleIndex;

    const updatedCabinet = {
      ...cabinet,
      modules: (cabinet.modules || []).filter(m => m.id !== moduleId),
    };

    setCabinets(prev => prev.map(c => c.id === selectedCabinetId ? updatedCabinet : c));

    if (selectedModuleId === moduleId) {
      setSelectedModuleId(null);
    }

    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/warehouse/cabinets/${selectedCabinetId}/`,
        transformCabinetToAPI(updatedCabinet),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const updated = transformCabinetFromAPI(response.data);
      setCabinets(prev => prev.map(c => c.id === updated.id ? updated : c));

      // Update selectedModuleId to the new ID from backend if another module was selected
      if (adjustedIndex >= 0 && updated.modules && updated.modules[adjustedIndex]) {
        setSelectedModuleId(updated.modules[adjustedIndex].id);
      }
    } catch (err) {
      console.error('Error deleting module:', err);
      fetchCabinets();
      alert('Failed to delete module. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedCabinetId, selectedModuleId, cabinets, fetchCabinets]);

  // Update module within cabinet
  const handleUpdateModule = useCallback(async (updatedModule) => {
    if (!selectedCabinetId) return;

    const cabinet = cabinets.find(c => c.id === selectedCabinetId);
    if (!cabinet) return;

    // Find the index of the module being updated to preserve selection after API response
    const moduleIndex = (cabinet.modules || []).findIndex(m => m.id === updatedModule.id);

    const updatedCabinet = {
      ...cabinet,
      modules: (cabinet.modules || []).map(m =>
        m.id === updatedModule.id ? updatedModule : m
      ),
    };

    // Optimistic update
    setCabinets(prev => prev.map(c => c.id === selectedCabinetId ? updatedCabinet : c));

    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/warehouse/cabinets/${selectedCabinetId}/`,
        transformCabinetToAPI(updatedCabinet),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const updated = transformCabinetFromAPI(response.data);
      setCabinets(prev => prev.map(c => c.id === updated.id ? updated : c));

      // Update selectedModuleId to the new ID from the backend response
      if (moduleIndex >= 0 && updated.modules && updated.modules[moduleIndex]) {
        setSelectedModuleId(updated.modules[moduleIndex].id);
      }
    } catch (err) {
      console.error('Error updating module:', err);
      fetchCabinets();
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedCabinetId, cabinets, fetchCabinets]);

  // Select cabinet
  const handleSelectCabinet = useCallback((cabinetId) => {
    setSelectedCabinetId(cabinetId);
    setSelectedModuleId(null);
    setViewMode('editor');
  }, []);

  // Select module within cabinet
  const handleSelectModule = useCallback((moduleId) => {
    setSelectedModuleId(moduleId);
  }, []);

  const handleBackToCabinetList = useCallback(() => {
    setSelectedCabinetId(null);
    setSelectedModuleId(null);
    setViewMode('cabinet-list');
  }, []);

  // Back to overview (cabinets list)
  const handleBackToOverview = useCallback(() => {
    setSelectedCabinetId(null);
    setSelectedModuleId(null);
    setViewMode('overview');
  }, []);

  // Toggle cabinet selection for print
  const toggleCabinetForPrint = useCallback((e, cabinetId) => {
    e.stopPropagation();
    setSelectedCabinetsForPrint(prev => {
      const next = new Set(prev);
      if (next.has(cabinetId)) {
        next.delete(cabinetId);
      } else {
        next.add(cabinetId);
      }
      return next;
    });
  }, []);

  // Select all cabinets for print
  const selectAllCabinetsForPrint = useCallback(() => {
    setSelectedCabinetsForPrint(new Set(cabinets.map(c => c.id)));
  }, [cabinets]);

  // Deselect all cabinets for print
  const deselectAllCabinetsForPrint = useCallback(() => {
    setSelectedCabinetsForPrint(new Set());
  }, []);

  // Delete cabinet from overview
  const handleDeleteCabinetFromOverview = useCallback(async (e, cabinetId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this cabinet and all its modules?')) {
      return;
    }
    await handleDeleteCabinet(cabinetId);
  }, [handleDeleteCabinet]);

  // Reorder modules within cabinet
  const handleReorderModule = useCallback(async (fromIndex, toIndex) => {
    if (!selectedCabinetId) return;

    const cabinet = cabinets.find(c => c.id === selectedCabinetId);
    if (!cabinet) return;

    const modules = [...(cabinet.modules || [])];
    const [moved] = modules.splice(fromIndex, 1);
    modules.splice(toIndex, 0, moved);

    // Find where the selected module ends up after reorder
    const selectedModuleNewIndex = selectedModuleId
      ? modules.findIndex(m => m.id === selectedModuleId)
      : -1;

    const updatedCabinet = { ...cabinet, modules };

    // Optimistic update
    setCabinets(prev => prev.map(c => c.id === selectedCabinetId ? updatedCabinet : c));

    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/warehouse/cabinets/${selectedCabinetId}/`,
        transformCabinetToAPI(updatedCabinet),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const updated = transformCabinetFromAPI(response.data);
      setCabinets(prev => prev.map(c => c.id === updated.id ? updated : c));

      // Update selectedModuleId to the new ID from backend
      if (selectedModuleNewIndex >= 0 && updated.modules && updated.modules[selectedModuleNewIndex]) {
        setSelectedModuleId(updated.modules[selectedModuleNewIndex].id);
      }
    } catch (err) {
      console.error('Error reordering modules:', err);
      fetchCabinets();
      alert('Failed to reorder modules. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedCabinetId, selectedModuleId, cabinets, fetchCabinets]);

  // Get current cabinet and module
  const selectedCabinet = cabinets.find(c => c.id === selectedCabinetId) || null;
  const selectedModule = selectedCabinet?.modules?.find(m => m.id === selectedModuleId) || null;

  // Calculate total stats
  const totalStats = {
    totalCabinets: cabinets.length,
    totalModules: cabinets.reduce((sum, c) => sum + (c.modules?.length || 0), 0),
    totalDrawers: cabinets.reduce((sum, c) =>
      sum + (c.modules || []).reduce((mSum, m) => mSum + (m.drawers?.length || 0), 0), 0),
    totalItems: cabinets.reduce((sum, c) =>
      sum + (c.modules || []).reduce((mSum, m) =>
        mSum + (m.drawers || []).reduce((dSum, d) => dSum + (d.items?.length || 0), 0), 0), 0),
  };

  // Print function
  const handlePrintAllCabinets = () => {
    // Filter cabinets based on selection
    const cabinetsToPrint = selectedCabinetsForPrint.size > 0
      ? cabinets.filter(c => selectedCabinetsForPrint.has(c.id))
      : cabinets;

    if (cabinetsToPrint.length === 0) {
      alert('No cabinets selected to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print');
      return;
    }

    const generateModuleHTML = (module, cabinetName) => {
      const drawers = module.drawers || [];
      return `
        <div class="module-section">
          <div class="module-header" style="border-left-color: ${module.color};">
            <h2>${module.name}</h2>
            <span class="module-meta">${drawers.length} drawer(s) • ${calculateUsedUnits(drawers)}/3 units</span>
          </div>
          ${drawers.length === 0 ? '<p class="no-content">No drawers in this module</p>' : drawers.map(drawer => `
            <div class="drawer-section">
              <div class="drawer-header">
                <span class="drawer-badge ${drawer.size}">${drawer.size?.charAt(0).toUpperCase() || 'S'}</span>
                <span class="drawer-name">${drawer.name}</span>
                <span class="drawer-units">(${drawer.units} unit${drawer.units !== 1 ? 's' : ''})</span>
              </div>
              ${drawer.image ? `<img src="${drawer.image}" alt="${drawer.name}" class="drawer-image" />` : ''}
              <div class="items-list">
                ${(drawer.items || []).length === 0 ? '<p class="no-items">No items in this drawer</p>' : (drawer.items || []).map(item => `
                  <div class="item-row">
                    ${item.image ? `<img src="${item.image}" alt="${item.name}" class="item-image" />` : '<div class="item-image-placeholder"></div>'}
                    <div class="item-details">
                      <div class="item-name">${item.name}</div>
                      ${item.description ? `<div class="item-desc">${item.description}</div>` : ''}
                    </div>
                    <span class="item-qty">x${item.quantity}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          `).join('')}
        </div>
      `;
    };

    const generateCabinetHTML = (cabinet, index) => {
      const modules = cabinet.modules || [];
      return `
        ${index > 0 ? '<div class="page-break"></div>' : ''}
        <div class="cabinet-section">
          <div class="cabinet-header">
            <h1>${cabinet.name}</h1>
            <span class="cabinet-meta">${modules.length} module(s)</span>
          </div>
          <div class="cabinet-content">
            ${modules.length === 0 ? '<p class="no-content">No modules in this cabinet</p>' : modules.map(m => generateModuleHTML(m, cabinet.name)).join('')}
          </div>
        </div>
      `;
    };

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cabinets Inventory Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 8px; color: #18181b; background: white; font-size: 11px; }
            .print-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid #f97316; }
            .print-header h1 { font-size: 16px; color: #0f172a; text-transform: uppercase; }
            .print-header .date { color: #64748b; font-size: 10px; }
            .page-break { page-break-before: always; margin-top: 15px; padding-top: 10px; border-top: 2px dashed #e2e8f0; }
            .cabinet-section { margin-bottom: 15px; border: 2px solid #1e293b; border-radius: 4px; overflow: hidden; }
            .cabinet-header { background: #1e293b; color: white; padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; }
            .cabinet-header h1 { font-size: 14px; text-transform: uppercase; margin: 0; font-weight: bold; }
            .cabinet-meta { font-size: 10px; opacity: 0.8; }
            .cabinet-content { padding: 8px; background: #fafafa; }
            .module-section { margin-bottom: 8px; border: 1px solid #e2e8f0; }
            .module-header { background: #334155; color: white; padding: 4px 8px; display: flex; justify-content: space-between; align-items: center; border-left: 3px solid; }
            .module-header h2 { font-size: 11px; text-transform: uppercase; margin: 0; }
            .module-meta { font-size: 9px; opacity: 0.8; }
            .drawer-section { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
            .drawer-section:last-child { border-bottom: none; }
            .drawer-header { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
            .drawer-badge { width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: bold; border-radius: 2px; }
            .drawer-badge.small { background: #dbeafe; color: #1d4ed8; }
            .drawer-badge.medium { background: #dcfce7; color: #15803d; }
            .drawer-badge.large { background: #fef3c7; color: #b45309; }
            .drawer-name { font-weight: 600; font-size: 11px; }
            .drawer-units { font-size: 9px; color: #64748b; }
            .drawer-image { max-width: 200px; max-height: 80px; object-fit: contain; margin: 4px 0; }
            .items-list { margin-top: 4px; }
            .item-row { display: flex; align-items: center; gap: 8px; padding: 3px 0; border-bottom: 1px solid #f1f5f9; }
            .item-row:last-child { border-bottom: none; }
            .item-image { width: 30px; height: 30px; object-fit: cover; border-radius: 2px; flex-shrink: 0; }
            .item-image-placeholder { width: 30px; height: 30px; background: #f1f5f9; border-radius: 2px; flex-shrink: 0; }
            .item-details { flex: 1; }
            .item-name { font-weight: 600; font-size: 10px; }
            .item-desc { font-size: 9px; color: #64748b; }
            .item-qty { background: #f1f5f9; padding: 1px 6px; border-radius: 2px; font-size: 10px; font-weight: 600; }
            .no-content, .no-items { color: #94a3b8; font-style: italic; font-size: 10px; padding: 4px 8px; }
            .footer { margin-top: 10px; padding-top: 6px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 8px; color: #94a3b8; }
            @media print {
              body { padding: 5px; }
              @page { margin: 5mm; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Cabinets Inventory Report</h1>
            <span class="date">${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</span>
          </div>
          ${cabinetsToPrint.map((cabinet, index) => generateCabinetHTML(cabinet, index)).join('')}
          <div class="footer">Cabinets Configurator • Inventory Management System</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // Print Checklist function
  const handlePrintChecklist = () => {
    // Filter cabinets based on selection
    const cabinetsToPrint = selectedCabinetsForPrint.size > 0
      ? cabinets.filter(c => selectedCabinetsForPrint.has(c.id))
      : cabinets;

    if (cabinetsToPrint.length === 0) {
      alert('No cabinets selected to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print');
      return;
    }

    // Generate unique checklist ID for tracking
    const checklistId = `CHK-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
    const generatedDate = new Date();
    const formattedDate = generatedDate.toLocaleDateString();
    const formattedTime = generatedDate.toLocaleTimeString();

    const generateModuleChecklistHTML = (module) => {
      const drawers = module.drawers || [];
      return `
        <div class="module-section">
          <div class="module-header" style="border-left-color: ${module.color};">
            <h2>${module.name}</h2>
            <span class="module-meta">${drawers.length} drawer(s)</span>
          </div>
          ${drawers.length === 0 ? '<p class="no-content">No drawers in this module</p>' : drawers.map(drawer => `
            <div class="drawer-section">
              <div class="drawer-header">
                <span class="drawer-badge ${drawer.size}">${drawer.size?.charAt(0).toUpperCase() || 'S'}</span>
                <span class="drawer-name">${drawer.name}</span>
              </div>
              <table class="checklist-table">
                <thead>
                  <tr>
                    <th class="col-check">OK</th>
                    <th class="col-item">Item</th>
                    <th class="col-expected">Expected</th>
                    <th class="col-actual">Actual</th>
                    <th class="col-notes">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  ${(drawer.items || []).length === 0 ? `
                    <tr><td colspan="5" class="no-items">No items in this drawer</td></tr>
                  ` : (drawer.items || []).map(item => `
                      <tr>
                        <td class="col-check">
                          <div class="checkbox"></div>
                        </td>
                        <td class="col-item">
                          <div class="item-name">${item.name}</div>
                          ${item.description ? `<div class="item-desc">${item.description}</div>` : ''}
                        </td>
                        <td class="col-expected">${item.quantity}</td>
                        <td class="col-actual">
                          <div class="input-field"></div>
                        </td>
                        <td class="col-notes">
                          <div class="notes-field"></div>
                        </td>
                      </tr>
                    `).join('')}
                </tbody>
              </table>
            </div>
          `).join('')}
        </div>
      `;
    };

    const generateCabinetChecklistHTML = (cabinet, index) => {
      const modules = cabinet.modules || [];
      return `
        ${index > 0 ? '<div class="page-break"></div>' : ''}
        <div class="cabinet-section">
          <div class="cabinet-header">
            <h1>${cabinet.name}</h1>
            <span class="cabinet-meta">${modules.length} module(s)</span>
          </div>
          <div class="cabinet-content">
            ${modules.length === 0 ? '<p class="no-content">No modules in this cabinet</p>' : modules.map(generateModuleChecklistHTML).join('')}
          </div>
        </div>
      `;
    };

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cabinet Inventory Checklist</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 8px; color: #18181b; background: white; font-size: 10px; }
            .print-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 2px solid #f97316; }
            .print-header h1 { font-size: 14px; color: #0f172a; text-transform: uppercase; }
            .print-header .header-right { text-align: right; }
            .print-header .checklist-id { font-size: 11px; font-weight: bold; color: #f97316; font-family: monospace; }
            .print-header .date { color: #64748b; font-size: 9px; }
            .info-row { display: flex; gap: 15px; margin-bottom: 8px; padding: 6px 8px; background: #f8fafc; border-radius: 4px; flex-wrap: wrap; }
            .info-field { display: flex; align-items: center; gap: 4px; }
            .info-label { font-weight: 600; font-size: 9px; color: #64748b; }
            .info-input { border-bottom: 1px solid #94a3b8; min-width: 80px; height: 14px; }
            .legend { display: flex; gap: 15px; margin-bottom: 8px; font-size: 9px; color: #64748b; }
            .legend-item { display: flex; align-items: center; gap: 3px; }
            .legend-checkbox { width: 10px; height: 10px; border: 1px solid #94a3b8; border-radius: 2px; }
            .legend-checkbox.checked { background: #16a34a; border-color: #16a34a; }
            .page-break { page-break-before: always; margin-top: 12px; padding-top: 8px; border-top: 2px dashed #e2e8f0; }
            .cabinet-section { margin-bottom: 12px; border: 2px solid #1e293b; border-radius: 4px; overflow: hidden; }
            .cabinet-header { background: #1e293b; color: white; padding: 6px 10px; display: flex; justify-content: space-between; align-items: center; }
            .cabinet-header h1 { font-size: 12px; text-transform: uppercase; margin: 0; font-weight: bold; }
            .cabinet-meta { font-size: 9px; opacity: 0.8; }
            .cabinet-content { padding: 6px; background: #fafafa; }
            .module-section { margin-bottom: 6px; border: 1px solid #e2e8f0; }
            .module-header { background: #334155; color: white; padding: 4px 6px; display: flex; justify-content: space-between; align-items: center; border-left: 3px solid; }
            .module-header h2 { font-size: 10px; text-transform: uppercase; margin: 0; }
            .module-meta { font-size: 8px; opacity: 0.8; }
            .drawer-section { padding: 4px 6px; border-bottom: 1px solid #e2e8f0; }
            .drawer-section:last-child { border-bottom: none; }
            .drawer-header { display: flex; align-items: center; gap: 4px; margin-bottom: 4px; padding-bottom: 3px; border-bottom: 1px dashed #e2e8f0; }
            .drawer-badge { width: 14px; height: 14px; display: flex; align-items: center; justify-content: center; font-size: 8px; font-weight: bold; border-radius: 2px; }
            .drawer-badge.small { background: #dbeafe; color: #1d4ed8; }
            .drawer-badge.medium { background: #dcfce7; color: #15803d; }
            .drawer-badge.large { background: #fef3c7; color: #b45309; }
            .drawer-name { font-weight: 600; font-size: 10px; }
            .checklist-table { width: 100%; border-collapse: collapse; }
            .checklist-table th { background: #f1f5f9; padding: 3px 4px; text-align: left; font-size: 8px; font-weight: 600; text-transform: uppercase; color: #64748b; }
            .checklist-table td { padding: 4px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
            .checklist-table tr:last-child td { border-bottom: none; }
            .col-check { width: 25px; text-align: center; }
            .col-item { width: 45%; }
            .col-expected { width: 45px; text-align: center; }
            .col-actual { width: 45px; text-align: center; }
            .col-notes { width: 20%; }
            .checkbox { width: 12px; height: 12px; border: 1px solid #94a3b8; border-radius: 2px; margin: 0 auto; }
            .item-name { font-weight: 500; font-size: 9px; }
            .item-desc { font-size: 8px; color: #64748b; }
            .input-field { border-bottom: 1px solid #c0c0c0; height: 12px; }
            .notes-field { border-bottom: 1px solid #c0c0c0; height: 12px; width: 100%; }
            .no-content, .no-items { color: #94a3b8; font-style: italic; font-size: 9px; padding: 4px; text-align: center; }
            .signature-section { margin-top: 12px; padding-top: 8px; border-top: 1px solid #e2e8f0; display: flex; gap: 20px; }
            .signature-box { flex: 1; }
            .signature-label { font-size: 8px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
            .signature-line { border-bottom: 1px solid #0f172a; height: 20px; margin-bottom: 2px; }
            .signature-sublabel { font-size: 7px; color: #94a3b8; }
            .footer { margin-top: 8px; padding-top: 4px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 7px; color: #94a3b8; }
            @media print {
              body { padding: 5px; }
              @page { margin: 5mm; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Cabinet Inventory Checklist</h1>
            <div class="header-right">
              <div class="checklist-id">ID: ${checklistId}</div>
              <div class="date">${formattedDate} ${formattedTime}</div>
            </div>
          </div>

          <div class="info-row">
            <div class="info-field"><span class="info-label">Checked by:</span><div class="info-input"></div></div>
            <div class="info-field"><span class="info-label">Date:</span><div class="info-input"></div></div>
            <div class="info-field"><span class="info-label">Location:</span><div class="info-input"></div></div>
          </div>

          <div class="legend">
            <div class="legend-item"><div class="legend-checkbox checked"></div><span>OK</span></div>
            <div class="legend-item"><div class="legend-checkbox"></div><span>Missing</span></div>
          </div>

          ${cabinetsToPrint.map((cabinet, index) => generateCabinetChecklistHTML(cabinet, index)).join('')}

          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-label">Inspector</div>
              <div class="signature-line"></div>
              <div class="signature-sublabel">Signature & Date</div>
            </div>
            <div class="signature-box">
              <div class="signature-label">Supervisor</div>
              <div class="signature-line"></div>
              <div class="signature-sublabel">Signature & Date</div>
            </div>
            <div class="signature-box">
              <div class="signature-label">Notes</div>
              <div class="signature-line"></div>
            </div>
          </div>

          <div class="footer">Checklist ${checklistId} • ${totalStats.totalCabinets} Cab. • ${totalStats.totalModules} Mod. • ${totalStats.totalDrawers} Draw. • ${totalStats.totalItems} Items</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // Render Overview (Cabinets list)
  const renderOverview = () => (
    <div className="qconfig-overview">
      <div className="qconfig-overview-header">
        <div className="qconfig-overview-header-left">
          <h2>My Cabinets</h2>
          <p>{totalStats.totalCabinets} cabinet{totalStats.totalCabinets !== 1 ? 's' : ''} configured</p>
        </div>
        <div className="qconfig-overview-header-right">
          {totalStats.totalCabinets > 0 && (
            <>
              {selectedCabinetsForPrint.size > 0 && (
                <span className="qconfig-selection-info">
                  {selectedCabinetsForPrint.size} selected
                </span>
              )}
              {selectedCabinetsForPrint.size > 0 && selectedCabinetsForPrint.size < cabinets.length && (
                <button onClick={selectAllCabinetsForPrint} className="qconfig-btn-text">
                  Select All
                </button>
              )}
              {selectedCabinetsForPrint.size > 0 && (
                <button onClick={deselectAllCabinetsForPrint} className="qconfig-btn-text">
                  Clear
                </button>
              )}
              <button
                onClick={handlePrintChecklist}
                className="qconfig-btn-secondary"
                disabled={selectedCabinetsForPrint.size === 0}
              >
                <ClipboardCheck className="w-4 h-4" />
                <span>Print Checklist</span>
              </button>
              <button
                onClick={handlePrintAllCabinets}
                className="qconfig-btn-secondary"
                disabled={selectedCabinetsForPrint.size === 0}
              >
                <Printer className="w-4 h-4" />
                <span>Print Report</span>
              </button>
            </>
          )}
          <button onClick={handleAddCabinet} className="qconfig-btn-primary" disabled={isSaving}>
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span>New Cabinet</span>
          </button>
        </div>
      </div>

      {cabinets.length > 0 && (
        <div className="qconfig-overview-stats">
          <div className="qconfig-overview-stat">
            <Box className="w-5 h-5" />
            <span className="value">{totalStats.totalCabinets}</span>
            <span className="label">Cabinets</span>
          </div>
          <div className="qconfig-overview-stat">
            <Layers className="w-5 h-5" />
            <span className="value">{totalStats.totalModules}</span>
            <span className="label">Modules</span>
          </div>
          <div className="qconfig-overview-stat">
            <Archive className="w-5 h-5" />
            <span className="value">{totalStats.totalDrawers}</span>
            <span className="label">Drawers</span>
          </div>
          <div className="qconfig-overview-stat">
            <Package className="w-5 h-5" />
            <span className="value">{totalStats.totalItems}</span>
            <span className="label">Items</span>
          </div>
        </div>
      )}

      {cabinets.length === 0 ? (
        <div className="qconfig-overview-empty">
          <div className="qconfig-overview-empty-icon">
            <LayoutGrid className="w-16 h-16" />
          </div>
          <h3>No Cabinets Yet</h3>
          <p>Create your first cabinet to start organizing your tools and equipment.</p>
          <button onClick={handleAddCabinet} className="qconfig-btn-primary" disabled={isSaving}>
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span>Create First Cabinet</span>
          </button>
        </div>
      ) : (
        <div className="qconfig-cabinets-grid">
          {cabinets.map((cabinet) => {
            const modules = cabinet.modules || [];
            const moduleCount = modules.length;
            const drawerCount = modules.reduce((sum, m) => sum + (m.drawers?.length || 0), 0);
            const itemCount = modules.reduce((sum, m) =>
              sum + (m.drawers || []).reduce((dSum, d) => dSum + (d.items?.length || 0), 0), 0);

            const isSelectedForPrint = selectedCabinetsForPrint.has(cabinet.id);

            return (
              <div
                key={cabinet.id}
                className={`qconfig-cabinet-card ${isSelectedForPrint ? 'selected-for-print' : ''}`}
                onClick={() => handleSelectCabinet(cabinet.id)}
              >
                <div className="qconfig-cabinet-checkbox">
                  <input
                    type="checkbox"
                    checked={isSelectedForPrint}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleCabinetForPrint(e, cabinet.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div
                  className="qconfig-cabinet-card-color"
                  style={{ backgroundColor: cabinet.color || '#0f172a' }}
                />
                <div className="qconfig-cabinet-card-content">
                  <h3>{cabinet.name}</h3>
                  <div className="qconfig-cabinet-card-stats">
                    <span>{moduleCount} module{moduleCount !== 1 ? 's' : ''}</span>
                    <span className="dot">&bull;</span>
                    <span>{drawerCount} drawer{drawerCount !== 1 ? 's' : ''}</span>
                    <span className="dot">&bull;</span>
                    <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                  </div>
                  {moduleCount > 0 && (
                    <div className="qconfig-cabinet-card-drawers">
                      {modules.slice(0, 3).map((module) => (
                        <div key={module.id} className="qconfig-cabinet-drawer-preview" style={{ borderLeftColor: module.color }}>
                          <span className="drawer-name">{module.name}</span>
                          <span className="module-info">{(module.drawers || []).length} drawers</span>
                        </div>
                      ))}
                      {moduleCount > 3 && (
                        <div className="qconfig-cabinet-more">+{moduleCount - 3} more</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="qconfig-cabinet-card-actions">
                  <button
                    className="qconfig-cabinet-edit-btn"
                    onClick={(e) => { e.stopPropagation(); handleSelectCabinet(cabinet.id); }}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    className="qconfig-cabinet-delete-btn"
                    onClick={(e) => handleDeleteCabinetFromOverview(e, cabinet.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          <div className="qconfig-cabinet-card qconfig-cabinet-card-add" onClick={handleAddCabinet}>
            <Plus className="w-8 h-8" />
            <span>Add Cabinet</span>
          </div>
        </div>
      )}
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="qconfig-container">
        <div className="qconfig-loading">
          <Loader2 className="w-12 h-12 animate-spin" />
          <p>Loading cabinets...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="qconfig-container">
        <div className="qconfig-error">
          <AlertCircle className="w-12 h-12" />
          <p>{error}</p>
          <button onClick={fetchCabinets} className="qconfig-btn-primary">
            <RefreshCw className="w-4 h-4" />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="qconfig-container">
      <div className="qconfig-header">
        <div className="qconfig-header-left">
          <button
            onClick={() => (viewMode === 'overview' || viewMode === 'cabinet-list') ? navigate('/warehouse/inventory') : handleBackToOverview()}
            className="qconfig-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="qconfig-title">
              {(viewMode === 'overview' || viewMode === 'cabinet-list')
                ? 'Configurator'
                : selectedCabinet?.name || 'Configurator'}
            </h1>
            <p className="qconfig-subtitle">
              {(viewMode === 'overview' || viewMode === 'cabinet-list')
                ? 'Tool Cabinet Configuration System'
                : viewMode === 'summary'
                  ? 'Inventory Summary Report'
                  : `Configure modules and drawers`}
            </p>
          </div>
        </div>
        <div className="qconfig-header-right">
          {isSaving && (
            <span className="qconfig-saving-indicator">
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </span>
          )}
          {viewMode !== 'overview' && viewMode !== 'cabinet-list' && (
            <button onClick={handleBackToOverview} className="qconfig-btn-secondary">
              <LayoutGrid className="w-4 h-4" />
              <span>All Cabinets</span>
            </button>
          )}
        </div>
      </div>

      {viewMode === 'overview' || viewMode === 'cabinet-list' ? (
        <div className="qconfig-main qconfig-main-overview">
          {renderOverview()}
        </div>
      ) : (
        <div className="qconfig-main">
          <QConfiguratorSidebar
            modules={selectedCabinet?.modules || []}
            selectedModuleId={selectedModuleId}
            viewMode={viewMode}
            onSelectModule={handleSelectModule}
            onAddModule={handleAddModule}
            onDeleteModule={handleDeleteModule}
            onViewSummary={() => setViewMode('summary')}
            onReorderModule={handleReorderModule}
            cabinetName={selectedCabinet?.name}
          />
          <div className="qconfig-content">
            {viewMode === 'editor' ? (
              selectedModule ? (
                <QConfiguratorModuleView
                  module={selectedModule}
                  cabinetName={selectedCabinet?.name}
                  onUpdateModule={handleUpdateModule}
                  onDeleteModule={() => {
                    handleDeleteModule(selectedModule.id);
                    setSelectedModuleId(null);
                  }}
                />
              ) : (
                <div className="qconfig-empty-state">
                  <div className="qconfig-empty-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <line x1="3" y1="9" x2="21" y2="9" />
                      <line x1="3" y1="15" x2="21" y2="15" />
                      <line x1="9" y1="3" x2="9" y2="21" />
                    </svg>
                  </div>
                  <h2>No Module Selected</h2>
                  <p>Create a new module or select an existing one from the sidebar.</p>
                  <button onClick={handleAddModule} className="qconfig-btn-primary">
                    Create First Module
                  </button>
                </div>
              )
            ) : (
              <QConfiguratorSummary
                modules={selectedCabinet?.modules || []}
                onEditModule={handleSelectModule}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QConfiguratorPro;
